import { useEffect, useMemo, useState } from "react";
import { fetchRecordsData } from "./useRecordsApi";

const getAbbrevName = (fullName = "") => {
    const [firstName, ...lastNames] = String(fullName).trim().split(" ");

    if (!firstName) {
        return "-";
    }

    if (!lastNames.length) {
        return firstName;
    }

    return `${firstName[0]}. ${lastNames.join(" ")}`;
};

const toCareerRows = (list = [], valueGetter) =>
    list.slice(0, 5).map((item, index) => ({
        id: `${item.nombre}-${index}`,
        rank: index + 1,
        name: item.nombreAbrev || getAbbrevName(item.nombre),
        slug: item.nombre,
        value: valueGetter(item),
    }));

const toMaxRows = (list = [], valueKey) =>
    list.slice(0, 5).map((item, index) => ({
        id: `${item?._id || item?.jugador?.jugador}-${index}`,
        rank: index + 1,
        name: getAbbrevName(item?.jugador?.jugador),
        slug: item?.jugador?.jugador,
        value: item?.jugador?.[valueKey] ?? "-",
        description: item?.adversario && item?.liga ? `vs ${item.adversario} · ${item.liga}` : "",
    }));

const sortTopFiveByMetric = (items = [], metric) =>
    items
        .slice()
        .sort((a, b) => Number(b?.torneo?.[metric] ?? 0) - Number(a?.torneo?.[metric] ?? 0))
        .slice(0, 5);

const sortTopFiveByPercentage = (items = [], metric) =>
    items
        .slice()
        .sort((a, b) => parseFloat(b?.torneo?.[metric] ?? 0) - parseFloat(a?.torneo?.[metric] ?? 0))
        .slice(0, 5);

const toSeasonRows = (list = [], metric) =>
    list.map((item, index) => ({
        id: `${item.slug}-${item.torneo?.liga}-${metric}-${index}`,
        rank: index + 1,
        name: item.name,
        slug: item.slug,
        value: item?.torneo?.[metric] ?? "-",
        description: item?.torneo?.liga || "",
    }));

const buildSeasonAverages = (promTemporada = []) => {
    const mpgCandidates = [];
    const ppgCandidates = [];
    const rtpgCandidates = [];
    const apgCandidates = [];
    const spgCandidates = [];
    const bpgCandidates = [];
    const tcpercCandidates = [];
    const tlpercCandidates = [];
    const tdpercCandidates = [];
    const ttpercCandidates = [];

    promTemporada.forEach((temporada) => {
        const torneosProm = temporada?.torneosProm || [];
        const torneos = temporada?.torneos || [];

        torneosProm.forEach((torneoProm, index) => {
            const torneoStats = torneos[index] || {};
            const liga = torneoProm?.liga || "";

            if (!liga.includes("LUB")) {
                return;
            }

            const row = {
                name: temporada.nombreAbrev || getAbbrevName(temporada.nombre),
                slug: temporada.nombre,
                torneo: torneoProm,
            };

            if (Number(torneoStats.partidos ?? 0) > 24) {
                mpgCandidates.push(row);
                ppgCandidates.push(row);
                rtpgCandidates.push(row);
                apgCandidates.push(row);
                spgCandidates.push(row);
                bpgCandidates.push(row);
            }

            if (Number(torneoStats.tcl ?? 0) > 200) {
                tcpercCandidates.push(row);
            }
            if (Number(torneoStats.tll ?? 0) > 150) {
                tlpercCandidates.push(row);
            }
            if (Number(torneoStats.tdl ?? 0) > 100) {
                tdpercCandidates.push(row);
            }
            if (Number(torneoStats.ttl ?? 0) > 100) {
                ttpercCandidates.push(row);
            }
        });
    });

    return {
        mpg: toSeasonRows(sortTopFiveByMetric(mpgCandidates, "mpg"), "mpg"),
        ppg: toSeasonRows(sortTopFiveByMetric(ppgCandidates, "ppg"), "ppg"),
        rtpg: toSeasonRows(sortTopFiveByMetric(rtpgCandidates, "rtpg"), "rtpg"),
        apg: toSeasonRows(sortTopFiveByMetric(apgCandidates, "apg"), "apg"),
        spg: toSeasonRows(sortTopFiveByMetric(spgCandidates, "spg"), "spg"),
        bpg: toSeasonRows(sortTopFiveByMetric(bpgCandidates, "bpg"), "bpg"),
        tcperc: toSeasonRows(sortTopFiveByPercentage(tcpercCandidates, "tcperc"), "tcperc"),
        tlperc: toSeasonRows(sortTopFiveByPercentage(tlpercCandidates, "tlperc"), "tlperc"),
        tdperc: toSeasonRows(sortTopFiveByPercentage(tdpercCandidates, "tdperc"), "tdperc"),
        ttperc: toSeasonRows(sortTopFiveByPercentage(ttpercCandidates, "ttperc"), "ttperc"),
    };
};

// Cached across the whole browser tab session so remounts (e.g. back navigation
// from a player profile) reuse the data instead of refetching from the APIs.
let recordsCache = null;
let recordsCachePromise = null;

function loadRecordsData() {
    if (recordsCache) {
        return Promise.resolve(recordsCache);
    }

    if (!recordsCachePromise) {
        recordsCachePromise = fetchRecordsData()
            .then((data) => {
                recordsCache = data;
                return data;
            })
            .catch((error) => {
                recordsCachePromise = null;
                throw error;
            });
    }

    return recordsCachePromise;
}

function useRecordsData() {
    const [isLoading, setIsLoading] = useState(!recordsCache);
    const [isError, setIsError] = useState(false);
    const [records, setRecords] = useState({});

    useEffect(() => {
        let isMounted = true;

        const load = async () => {
            if (!recordsCache) {
                setIsLoading(true);
            }
            setIsError(false);

            try {
                const [
                    partidosJugador,
                    ganadosJugador,
                    minJugador,
                    ptsJugador,
                    rtJugador,
                    astJugador,
                    stlJugador,
                    blsJugador,
                    minPromJugador,
                    ptsPromJugador,
                    rtPromJugador,
                    astPromJugador,
                    stlPromJugador,
                    blsPromJugador,
                    campoPercPromJugador,
                    librePercPromJugador,
                    doblePercPromJugador,
                    triplePercPromJugador,
                    puntosMaximo,
                    rebotesTotMaximo,
                    asistenciasMaximo,
                    robosMaximo,
                    tapasMaximo,
                    libresHechosMaximo,
                    doblesHechosMaximo,
                    triplesHechosMaximo,
                    promTemporada,
                    partidosDTJugador,
                    ganadosDTJugador,
                    winPercDTJugador,
                ] = await loadRecordsData();

                if (!isMounted) {
                    return;
                }

                setRecords({
                    partidosJugador,
                    ganadosJugador,
                    minJugador,
                    ptsJugador,
                    rtJugador,
                    astJugador,
                    stlJugador,
                    blsJugador,
                    minPromJugador,
                    ptsPromJugador,
                    rtPromJugador,
                    astPromJugador,
                    stlPromJugador,
                    blsPromJugador,
                    campoPercPromJugador,
                    librePercPromJugador,
                    doblePercPromJugador,
                    triplePercPromJugador,
                    puntosMaximo,
                    rebotesTotMaximo,
                    asistenciasMaximo,
                    robosMaximo,
                    tapasMaximo,
                    libresHechosMaximo,
                    doblesHechosMaximo,
                    triplesHechosMaximo,
                    promTemporada,
                    partidosDTJugador,
                    ganadosDTJugador,
                    winPercDTJugador,
                });
            } catch (error) {
                if (isMounted) {
                    setIsError(true);
                }
            } finally {
                if (isMounted) {
                    setIsLoading(false);
                }
            }
        };

        load();

        return () => {
            isMounted = false;
        };
    }, []);

    const seasonAverages = useMemo(
        () => buildSeasonAverages(records.promTemporada || []),
        [records.promTemporada]
    );

    const careerTotalSections = useMemo(() => ([
        {
            id: "totales-carrera",
            title: "Totales en carrera",
            cards: [
                {
                    id: "partidos-jugados",
                    title: "Partidos Jugados",
                    rows: toCareerRows(records.partidosJugador, (item) => item?.carrera?.partidos ?? "-"),
                    noDecimals: true,
                },
                {
                    id: "partidos-ganados",
                    title: "Partidos Ganados",
                    rows: toCareerRows(records.ganadosJugador, (item) => item?.carrera?.ganados ?? "-"),
                    noDecimals: true,
                },
                {
                    id: "minutos-carrera",
                    title: "Minutos",
                    rows: toCareerRows(records.minJugador, (item) => item?.carrera?.min ?? "-"),
                    noDecimals: true,
                },
                {
                    id: "puntos",
                    title: "Puntos",
                    rows: toCareerRows(records.ptsJugador, (item) => item?.carrera?.pts ?? "-"),
                    noDecimals: true,
                },
                {
                    id: "rebotes-totales",
                    title: "Rebotes",
                    rows: toCareerRows(records.rtJugador, (item) => item?.carrera?.rt ?? "-"),
                    noDecimals: true,
                },
                {
                    id: "asistencias-carrera",
                    title: "Asistencias",
                    rows: toCareerRows(records.astJugador, (item) => item?.carrera?.ast ?? "-"),
                    noDecimals: true,
                },
                {
                    id: "robos-carrera",
                    title: "Robos",
                    rows: toCareerRows(records.stlJugador, (item) => item?.carrera?.stl ?? "-"),
                    noDecimals: true,
                },
                {
                    id: "tapas-carrera",
                    title: "Tapas",
                    rows: toCareerRows(records.blsJugador, (item) => item?.carrera?.bls ?? "-"),
                    noDecimals: true,
                },
            ],
        },
    ]), [records]);

    const careerAverageSections = useMemo(() => ([
        {
            id: "promedios-carrera",
            title: "Promedios en carrera",
            subtitle: "Mínimo 30 partidos jugados",
            cards: [
                {
                    id: "puntos-promedio",
                    title: "Puntos",
                    rows: toCareerRows(records.ptsPromJugador, (item) => item?.carreraProm?.ppg ?? "-"),
                },
                {
                    id: "rebotes-promedio",
                    title: "Rebotes",
                    rows: toCareerRows(records.rtPromJugador, (item) => item?.carreraProm?.rtpg ?? "-"),
                },
                {
                    id: "asistencias-promedio",
                    title: "Asistencias",
                    rows: toCareerRows(records.astPromJugador, (item) => item?.carreraProm?.apg ?? "-"),
                },
                {
                    id: "robos-promedio",
                    title: "Robos",
                    rows: toCareerRows(records.stlPromJugador, (item) => item?.carreraProm?.spg ?? "-"),
                },
                {
                    id: "tapas-promedio",
                    title: "Tapas",
                    rows: toCareerRows(records.blsPromJugador, (item) => item?.carreraProm?.bpg ?? "-"),
                },
                {
                    id: "campo-promedio",
                    title: "Campo %",
                    rows: toCareerRows(records.campoPercPromJugador, (item) => item?.carreraProm?.tcperc ?? "-"),
                },
                {
                    id: "libres-promedio",
                    title: "Libres %",
                    rows: toCareerRows(records.librePercPromJugador, (item) => item?.carreraProm?.tlperc ?? "-"),
                },
                {
                    id: "dobles-promedio",
                    title: "Dobles %",
                    rows: toCareerRows(records.doblePercPromJugador, (item) => item?.carreraProm?.tdperc ?? "-"),
                },
                {
                    id: "triples-promedio",
                    title: "Triples %",
                    rows: toCareerRows(records.triplePercPromJugador, (item) => item?.carreraProm?.ttperc ?? "-"),
                },
            ],
        },
    ]), [records]);

    const gameMaxSections = useMemo(() => ([
        {
            id: "maximos-partidos",
            title: "Máximos en partidos",
            cards: [
                {
                    id: "puntos-maximos",
                    title: "Puntos",
                    rows: toMaxRows(records.puntosMaximo, "puntos"),
                    noDecimals: true,
                },
                {
                    id: "rebotes-maximos",
                    title: "Rebotes",
                    rows: toMaxRows(records.rebotesTotMaximo, "rebotesTot"),
                    noDecimals: true,
                },
                {
                    id: "asistencias-maximas",
                    title: "Asistencias",
                    rows: toMaxRows(records.asistenciasMaximo, "asistencias"),
                    noDecimals: true,
                },
                {
                    id: "robos-maximos",
                    title: "Robos",
                    rows: toMaxRows(records.robosMaximo, "robos"),
                    noDecimals: true,
                },
                {
                    id: "tapas-maximas",
                    title: "Tapas",
                    rows: toMaxRows(records.tapasMaximo, "tapas"),
                    noDecimals: true,
                },
                {
                    id: "libres-maximos",
                    title: "Libres Hechos",
                    rows: toMaxRows(records.libresHechosMaximo, "libresHechos"),
                    noDecimals: true,
                },
                {
                    id: "dobles-maximos",
                    title: "Dobles Hechos",
                    rows: toMaxRows(records.doblesHechosMaximo, "doblesHechos"),
                    noDecimals: true,
                },
                {
                    id: "triples-maximos",
                    title: "Triples Hechos",
                    rows: toMaxRows(records.triplesHechosMaximo, "triplesHechos"),
                    noDecimals: true,
                },
            ],
        },
    ]), [records]);

    const seasonAverageSections = useMemo(() => ([
        {
            id: "promedios-temporada",
            title: "Promedios en temporada",
            cards: [
                {
                    id: "puntos-temporada",
                    title: "Puntos",
                    rows: seasonAverages.ppg,
                },
                {
                    id: "rebotes-temporada",
                    title: "Rebotes",
                    rows: seasonAverages.rtpg,
                },
                {
                    id: "asistencias-temporada",
                    title: "Asistencias",
                    rows: seasonAverages.apg,
                },
                {
                    id: "robos-temporada",
                    title: "Robos",
                    rows: seasonAverages.spg,
                },
                {
                    id: "tapas-temporada",
                    title: "Tapas",
                    rows: seasonAverages.bpg,
                },
                {
                    id: "campo-temporada",
                    title: "Campo %",
                    rows: seasonAverages.tcperc,
                },
                {
                    id: "libres-temporada",
                    title: "Libres %",
                    rows: seasonAverages.tlperc,
                },
                {
                    id: "dobles-temporada",
                    title: "Dobles %",
                    rows: seasonAverages.tdperc,
                },
                {
                    id: "triples-temporada",
                    title: "Triples %",
                    rows: seasonAverages.ttperc,
                },
            ],
        },
    ]), [seasonAverages]);

    const dtSections = useMemo(() => ([
        {
            id: "records-dt",
            title: "Récords DT",
            subtitle: "Mínimo 30 partidos dirigidos",
            cards: [
                {
                    id: "partidos-dirigidos",
                    title: "Partidos Dirigidos",
                    rows: toCareerRows(records.partidosDTJugador, (item) => item?.carrera?.partidos ?? "-"),
                    noDecimals: true,
                },
                {
                    id: "partidos-ganados-dt",
                    title: "Partidos Ganados",
                    rows: toCareerRows(records.ganadosDTJugador, (item) => item?.carrera?.ganados ?? "-"),
                    noDecimals: true,
                },
                {
                    id: "win-perc-dt",
                    title: "Gan %",
                    rows: toCareerRows(records.winPercDTJugador, (item) => item?.carrera?.winPerc ?? "-"),
                },
            ],
        },
    ]), [records]);

    const modeOptions = [
        { id: "totales-carrera", label: "Totales en carrera" },
        { id: "promedios-carrera", label: "Promedios en carrera" },
        { id: "maximos-partidos", label: "Máximos en partidos" },
        { id: "promedios-temporada", label: "Promedios en temporada" },
        { id: "records-dt", label: "Récords DT" },
    ];

    const modeSections = {
        "totales-carrera": careerTotalSections,
        "promedios-carrera": careerAverageSections,
        "maximos-partidos": gameMaxSections,
        "promedios-temporada": seasonAverageSections,
        "records-dt": dtSections,
    };

    return {
        isLoading,
        isError,
        modeOptions,
        modeSections,
    };
}

export default useRecordsData;
